import { useEffect, useRef, useState } from 'react';
import type { PunchBicType, PunchStatus } from '@/lib/apiClient';
import { STATUS_LABELS } from './punchModel';
import { useOutdoorMode } from './useOutdoorMode';
import { usePhoneLayout } from './usePhoneLayout';
import { readVideoDuration, videoProblem, clipLength, fileSize, VIDEO_MAX_SECONDS } from './punchVideos';

/** Status: a pill with a dot, label always shown (never color alone). */
export function StatusBadge({ status }: { status: PunchStatus }) {
  return <span className={`pl-badge ${status}`}>{STATUS_LABELS[status]}</span>;
}

/** Ball in court: SQUARED, so it never reads as the pill status badge. */
export function BicChip({ type, label, large = false }: { type: PunchBicType; label: string; large?: boolean }) {
  return <span className={`pl-bic ${type}${large ? ' lg' : ''}`} title={label}>{label}</span>;
}

/** The sun button: outdoor high-contrast mode on or off (phone screens). */
export function OutdoorToggle() {
  const [on, toggle] = useOutdoorMode();
  return (
    <button type="button" className={`pl-outdoor-btn${on ? ' on' : ''}`} aria-pressed={on}
      aria-label="Outdoor mode (high contrast)" title="Outdoor mode (high contrast)" onClick={toggle}>
      ☀
    </button>
  );
}

/** Numbered squares joined by lines: past done (✓), current dark, future outlined. */
export function Stepper({ steps, current, className = 'pl-steps' }: {
  steps: string[]; current: number; className?: string;
}) {
  return (
    <div className={className} aria-label={`Step ${current + 1} of ${steps.length}: ${steps[current]}`}>
      {steps.map((s, i) => (
        <span key={s} style={{ display: 'contents' }}>
          {i > 0 && <span className="pl-step-line" aria-hidden />}
          <span className={`pl-step${i < current ? ' done' : i === current ? ' current' : ''}`}>
            <span className="pl-step-box" aria-hidden>{i < current ? '✓' : i + 1}</span>
            {s}
          </span>
        </span>
      ))}
    </div>
  );
}

/** A local preview URL for a picked file, revoked when the file changes or the component goes. */
export function useObjectUrl(file: File | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) { setUrl(null); return; }
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return url;
}

/**
 * A picked clip, with its length read from the file and why it can't go on,
 * if it can't. `recorded`: just filmed with the camera, so "now, here" is
 * true of it; a chosen clip's own time and place aren't known. `checking`:
 * its length is still being read — nothing may submit it yet.
 */
export interface PickedVideo { file: File; seconds: number | null; problem: string | null; recorded: boolean; checking: boolean }

/** A clip that can't be submitted yet: still being checked, or turned away. */
export function videoBlocks(v: PickedVideo | null): boolean {
  return !!v && (v.checking || !!v.problem);
}

/**
 * Record a clip with the phone's own camera (as photos are) or choose one,
 * and say at once whether it can go on: its length is read from the file,
 * so a clip over the limit is turned away before anything uploads.
 */
export function VideoPicker({ label, picked, onChange, busy = false }: {
  label: string; picked: PickedVideo | null; onChange: (v: PickedVideo | null) => void; busy?: boolean;
}) {
  const phone = usePhoneLayout();
  const recordRef = useRef<HTMLInputElement>(null);
  const chooseRef = useRef<HTMLInputElement>(null);
  // On a phone, the phone's own buttons: touch targets are 44px or more there (design).
  const btnClass = phone ? 'pl-phone-btn secondary' : 'btn btn-secondary btn-sm';
  // The pick is handed up at once, marked `checking`, so the form can't save
  // without it (or with the clip it replaced) while its length is read —
  // that can take seconds (Codex on #593). Only the latest pick or Clear
  // counts: a check finishing late mustn't bring back a clip since replaced
  // or cleared.
  const seq = useRef(0);
  const reading = !!picked?.checking;
  const pick = async (file: File | null, recorded: boolean) => {
    if (!file) return;
    const mine = ++seq.current;
    onChange({ file, seconds: null, problem: null, recorded, checking: true });
    const seconds = await readVideoDuration(file);
    if (mine !== seq.current) return;
    onChange({ file, seconds, problem: videoProblem(file, seconds), recorded, checking: false });
  };
  const clear = () => { seq.current++; onChange(null); };
  const input = (ref: typeof recordRef, capture: boolean) => (
    <input ref={ref} type="file" accept="video/*" hidden {...(capture ? { capture: 'environment' as const } : {})}
      onChange={(e) => { void pick(e.target.files?.[0] ?? null, capture); e.target.value = ''; }} />
  );
  return (
    <div className="pl-field">
      <span>{label}</span>
      <div className="pl-row">
        {phone && (
          <button type="button" className={btnClass} disabled={busy || reading} onClick={() => recordRef.current?.click()}>
            Record a Video
          </button>
        )}
        <button type="button" className={btnClass} disabled={busy || reading} onClick={() => chooseRef.current?.click()}>
          {picked ? 'Choose a Different Video' : 'Choose a Video'}
        </button>
        {picked && !busy && <button type="button" className="pl-link" onClick={clear}>Clear</button>}
      </div>
      {picked && (
        <div className="pl-muted">{[picked.file.name, clipLength(picked.seconds), fileSize(picked.file.size)].filter(Boolean).join(' · ')}</div>
      )}
      {reading && <div className="pl-muted">Checking the clip…</div>}
      {picked?.problem && <div className="pl-error" role="alert">{picked.problem}</div>}
      {/* Divs, not spans: a .pl-field's spans are styled as its label. */}
      <div className="pl-muted">Up to {VIDEO_MAX_SECONDS} seconds. A video is extra — it doesn’t replace the photos.</div>
      {input(recordRef, true)}
      {input(chooseRef, false)}
    </div>
  );
}

/**
 * One photo picked from the device (camera on a phone, a file on desktop),
 * with a local preview. The file is uploaded by whoever submits the form.
 */
export function PhotoPicker({ label, file, onChange, required = false }: {
  label: string; file: File | null; onChange: (f: File | null) => void; required?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const url = useObjectUrl(file);
  return (
    <div className="pl-field">
      <span>{label}{required ? ' · required' : ''}</span>
      {url && <img className="pl-preview" src={url} alt={`${label} preview`} />}
      <div className="pl-row">
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => inputRef.current?.click()}>
          {file ? 'Choose a different photo' : 'Choose photo'}
        </button>
        {file && <span className="pl-muted">{file.name}</span>}
      </div>
      <input
        ref={inputRef} type="file" accept="image/*" capture="environment" hidden
        onChange={(e) => { onChange(e.target.files?.[0] ?? null); e.target.value = ''; }}
      />
    </div>
  );
}
